import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  ServiceCategory,
  CreateServiceCategoryRequest,
  UpdateServiceCategoryRequest,
  ProfessionalService,
  CreateProfessionalServiceRequest,
  UpdateProfessionalServiceRequest,
  VerifyServiceRequest,
  ServiceFilterParams,
  ServiceSearchParams,
  PaginatedResponse,
  PaginationMeta,
  User,
  Payment,
  PaymentFilterParams,
  PaymentStats,
  PayoutRequest,
  ApprovePayoutRequest,
  Wallet,
  WalletTransaction,
  CommissionStats,
  FinancialReport,
  SubscriptionPlan,
  CreateSubscriptionPlanRequest,
  UpdateSubscriptionPlanRequest,
  Subscription,
  SubscriptionStatistics,
  Lead,
  LeadStatistics,
  SLAMonitoring,
  ContactRevealEvent,
  EventStatistics,
  BookingAnalytics,
  Document,
  ProfileApprovalRequest,
  AdminComment,
  CreateAdminCommentRequest,
  AdminActivityLog,
  NotificationEntry,
  ProfessionalReplyNotificationEntry,
  PaymentStatistics,
  PaymentTransactionsResponse,
  PermissionLookup,
  RoleDetails,
  City,
} from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    // Get API base URL from environment variable
    // Supports both subdomain and port-based configurations
    // Priority: REACT_APP_API_BASE_URL > auto-detect from window.location > fallback
    let apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    
    if (!apiBaseUrl) {
      // Auto-detect API URL based on current hostname
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        const protocol = window.location.protocol;
        
        // If accessing via subdomain (e.g., admin.yourdomain.com)
        if (hostname.includes('admin.') || hostname.includes('app.')) {
          const domain = hostname.replace(/^(admin|app)\./, '');
          apiBaseUrl = `${protocol}//api.${domain}/api/v1`;
        }
        // If accessing via localhost in development
        else if (hostname === 'localhost' || hostname === '127.0.0.1') {
          apiBaseUrl = 'http://localhost:8008/api/v1';
        }
        // Production fallback (should not reach here if env var is set)
        else {
          apiBaseUrl = `${protocol}//api.${hostname}/api/v1`;
        }
        
        // Log in production for debugging (can be removed later)
        console.log('🔧 API Base URL Auto-detected:', {
          hostname,
          protocol,
          apiBaseUrl,
          envVar: process.env.REACT_APP_API_BASE_URL || 'NOT SET'
        });
        
        // Development warning if env var is not set
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '⚠️ REACT_APP_API_BASE_URL not set. Auto-detected:', 
            apiBaseUrl,
            '\n💡 Set REACT_APP_API_BASE_URL in .env for explicit configuration.'
          );
        }
      } else {
        // Server-side rendering fallback (should not happen in React)
        apiBaseUrl = process.env.NODE_ENV === 'production' 
          ? 'https://api.yourdomain.com/api/v1'
          : 'http://localhost:8008/api/v1';
      }
    } else {
      console.log('✅ Using REACT_APP_API_BASE_URL:', apiBaseUrl);
    }
    
    // Validate API base URL
    if (!apiBaseUrl || apiBaseUrl.includes('yourdomain.com')) {
      console.error('❌ Invalid API Base URL:', apiBaseUrl);
      console.error('Please set REACT_APP_API_BASE_URL environment variable');
    }
    
    this.api = axios.create({
      baseURL: apiBaseUrl,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });
    
    // Add request logging in development
    if (process.env.NODE_ENV === 'development') {
      this.api.interceptors.request.use(
        (config) => {
          console.log('📤 API Request:', {
            method: config.method?.toUpperCase(),
            url: config.url,
            baseURL: config.baseURL,
            fullURL: `${config.baseURL}${config.url}`
          });
          return config;
        },
        (error) => Promise.reject(error)
      );
    }

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle 429 Too Many Requests (Rate Limit)
        if (error.response?.status === 429) {
          const retryAfter = error.response.headers['retry-after'] || error.response.headers['Retry-After'];
          const retryAfterSeconds = retryAfter ? parseInt(retryAfter, 10) : 60;
          
          console.error('⚠️ Rate limit exceeded (429):', {
            message: error.response?.data?.message || 'Too many requests. Please wait before trying again.',
            retryAfter: retryAfterSeconds,
            url: error.config?.url,
          });
          
          // Show user-friendly error message
          if (typeof window !== 'undefined') {
            const errorMessage = error.response?.data?.message || 
              `Too many requests. Please wait ${retryAfterSeconds} seconds before trying again.`;
            
            // You can customize this to show a toast notification instead
            console.warn('Rate Limit Error:', errorMessage);
          }
          
          // Return a more descriptive error
          const rateLimitError = new Error(
            error.response?.data?.message || 
            `Rate limit exceeded. Please wait ${retryAfterSeconds} seconds before trying again.`
          );
          (rateLimitError as any).status = 429;
          (rateLimitError as any).retryAfter = retryAfterSeconds;
          return Promise.reject(rateLimitError);
        }
        
        // Only redirect to login for 401 errors on authenticated requests
        // Don't redirect if we're already on the login page or if it's a login request
        // Also don't redirect if it's the initial auth check (GET /user) - let AuthContext handle it
        if (error.response?.status === 401) {
          const requestUrl = error.config?.url || '';
          const requestMethod = error.config?.method?.toLowerCase() || '';
          
          // Check if this is a login request - check both URL pattern and method
          const isLoginRequest = 
            requestUrl.includes('/auth/login') || 
            requestUrl.endsWith('/login') ||
            (requestMethod === 'post' && requestUrl.includes('login'));
          
          // Check if this is the initial auth verification request (GET /user)
          // AuthContext handles this case, so don't redirect
          const isAuthCheck = 
            requestMethod === 'get' && 
            (requestUrl === '/user' || requestUrl.endsWith('/user'));
          
          const isOnLoginPage = typeof window !== 'undefined' && 
            (window.location.pathname === '/login' || window.location.pathname.endsWith('/login'));
          
          // Debug logging (remove in production if needed)
          if (process.env.NODE_ENV === 'development') {
            console.log('401 Error Handler:', {
              requestUrl,
              requestMethod,
              isLoginRequest,
              isAuthCheck,
              isOnLoginPage,
              currentPath: typeof window !== 'undefined' ? window.location.pathname : 'N/A'
            });
          }
          
          // Check if we're in the initial auth restoration phase (first 2 seconds after page load)
          // During this time, don't clear auth on 401s - let AuthContext handle it
          const isInitialLoad = typeof window !== 'undefined' && 
            sessionStorage.getItem('auth_initializing') === 'true';
          
          // Only clear storage and redirect if it's not a login attempt, not an auth check, 
          // not on login page, and not during initial load
          // Login attempts, auth checks, and initial load should be handled by their respective components
          if (!isLoginRequest && !isAuthCheck && !isOnLoginPage && !isInitialLoad) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            // Use window.location for full page reload to clear React state
            // This ensures we're using the React Router route, not a server route
            if (typeof window !== 'undefined') {
              window.location.href = window.location.origin + '/login';
            }
          } else if (isInitialLoad) {
            console.log('⚠️ API: 401 during initial load, not clearing auth - letting AuthContext handle it');
          }
          // If it's a login request or auth check, just reject the error so the component can handle it
          // Don't redirect or clear storage - let the component show the error message or handle auth state
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication API
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response: AxiosResponse<ApiResponse<LoginResponse>> = await this.api.post('/auth/admin/login', credentials);
    return response.data.data;
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout');
  }

  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.get('/auth/me');
    return response.data.data.user;
  }

  // User Management (Admin only)
  async getUsers(params?: { 
    user_type?: string; 
    per_page?: number; 
    page?: number; 
    search?: string;
    is_verified?: boolean;
    is_active?: boolean;
  }): Promise<PaginatedResponse<User>> {
    const response: AxiosResponse<ApiResponse<{ users: User[], pagination: any }>> = await this.api.get('/users', {
      params
    });
    return {
      data: response.data.data.users || [],
      pagination: response.data.data.pagination || {}
    };
  }

  async getUser(id: number): Promise<User> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.get(`/users/${id}`);
    return response.data.data.user;
  }

  async deleteUser(userId: number): Promise<void> {
    await this.api.delete(`/users/${userId}`);
  }

  async getAdminPortalUsers(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    is_active?: boolean;
    is_verified?: boolean;
  }): Promise<PaginatedResponse<User>> {
    const response: AxiosResponse<ApiResponse<{ users: User[]; pagination: PaginationMeta }>> =
      await this.api.get('/admin/users', { params });

    return {
      data: response.data.data.users || [],
      pagination: response.data.data.pagination || {
        current_page: 1,
        per_page: params?.per_page || 15,
        total: 0,
        last_page: 1,
      },
    };
  }

  async getAdminUser(id: number): Promise<User> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.get(`/admin/users/${id}`);
    return response.data.data.user;
  }

  async createAdminUser(payload: {
    name: string;
    email: string;
    phone: string;
    password: string;
    is_active?: boolean;
    is_verified?: boolean;
    roles?: string[];
  }): Promise<User> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.post('/admin/users', payload);
    return response.data.data.user;
  }

  async updateAdminUser(id: number, payload: {
    name?: string;
    email?: string;
    phone?: string;
    password?: string;
    is_active?: boolean;
    is_verified?: boolean;
    roles?: string[];
  }): Promise<User> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.put(`/admin/users/${id}`, payload);
    return response.data.data.user;
  }

  async deleteAdminUser(id: number): Promise<void> {
    await this.api.delete(`/admin/users/${id}`);
  }

  async getAdminRoles(): Promise<string[]> {
    const response: AxiosResponse<ApiResponse<{ roles: string[] }>> = await this.api.get('/admin/roles/available');
    return response.data.data.roles || [];
  }

  async getUserStatistics(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/users/statistics');
    return response.data.data;
  }

  async getSettingsCompleteness(userId: number): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(
      `/users/${userId}/settings-completeness`
    );
    return response.data.data;
  }

  async approveExceptionVerification(
    userId: number,
    reason: string,
    expiresAt: string
  ): Promise<User> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.post(
      `/users/${userId}/exception-verification`,
      { reason, expires_at: expiresAt }
    );
    return response.data.data.user;
  }

  async updateUserVerification(userId: number, isVerified: boolean): Promise<User> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.patch(`/users/${userId}/verification`, {
      is_verified: isVerified,
    });
    return response.data.data.user;
  }

  async updateUserActiveStatus(userId: number, isActive: boolean): Promise<User> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.patch(`/users/${userId}/active-status`, {
      is_active: isActive,
    });
    return response.data.data.user;
  }

  async getPermissions(): Promise<PermissionLookup[]> {
    const response: AxiosResponse<ApiResponse<{ permissions: PermissionLookup[] }>> = await this.api.get('/admin/permissions');
    return response.data.data.permissions;
  }

  async createPermission(name: string): Promise<PermissionLookup> {
    const response: AxiosResponse<ApiResponse<{ permission: PermissionLookup }>> = await this.api.post('/admin/permissions', { name });
    return response.data.data.permission;
  }

  async getRoles(): Promise<RoleDetails[]> {
    const response: AxiosResponse<ApiResponse<{ roles: RoleDetails[] }>> = await this.api.get('/admin/roles');
    return response.data.data.roles;
  }

  async createRole(name: string, permissions: string[]): Promise<RoleDetails> {
    const response: AxiosResponse<ApiResponse<{ role: RoleDetails }>> = await this.api.post('/admin/roles', {
      name,
      permissions,
    });
    return response.data.data.role;
  }

  async updateRole(roleId: number, name: string, permissions: string[]): Promise<RoleDetails> {
    const response: AxiosResponse<ApiResponse<{ role: RoleDetails }>> = await this.api.put(`/admin/roles/${roleId}`, {
      name,
      permissions,
    });
    return response.data.data.role;
  }

  async deleteRole(roleId: number): Promise<void> {
    await this.api.delete(`/admin/roles/${roleId}`);
  }

  async getUserRoles(userId: number): Promise<{ role_names: string[]; available_roles: string[] }> {
    const response: AxiosResponse<ApiResponse<{ role_names: string[]; available_roles: string[] }>> =
      await this.api.get(`/admin/users/${userId}/roles`);
    return response.data.data;
  }

  async assignUserRoles(userId: number, roles: string[]): Promise<string[]> {
    const response: AxiosResponse<ApiResponse<{ roles: string[] }>> = await this.api.post(`/admin/users/${userId}/roles`, {
      roles,
    });
    return response.data.data.roles;
  }

  async getProfessionalReplies(params?: {
    page?: number;
    per_page?: number;
    comment_type?: string;
    professional_id?: number;
    source?: string;
    message?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<{ data: ProfessionalReplyNotificationEntry[]; pagination: PaginationMeta; last_seen?: string | null }> {
    const response: AxiosResponse<ApiResponse<{ logs: ProfessionalReplyNotificationEntry[]; pagination: PaginationMeta; last_seen: string | null }>> = await this.api.get('/admin/professional-replies', {
      params,
    });
    return {
      data: response.data.data.logs,
      pagination: response.data.data.pagination,
      last_seen: response.data.data.last_seen,
    };
  }

  // Service Categories API
  async getServiceCategories(includeInactive = false): Promise<ServiceCategory[]> {
    const response: AxiosResponse<ApiResponse<{ categories: ServiceCategory[], pagination: any }>> = await this.api.get('/service-categories', {
      params: { include_inactive: includeInactive }
    });
    return response.data.data.categories;
  }

  async getServiceCategory(id: number): Promise<ServiceCategory> {
    const response: AxiosResponse<ApiResponse<ServiceCategory>> = await this.api.get(`/service-categories/${id}`);
    return response.data.data;
  }

  async createServiceCategory(data: CreateServiceCategoryRequest): Promise<ServiceCategory> {
    const response: AxiosResponse<ApiResponse<ServiceCategory>> = await this.api.post('/service-categories', data);
    return response.data.data;
  }

  async updateServiceCategory(id: number, data: UpdateServiceCategoryRequest): Promise<ServiceCategory> {
    const response: AxiosResponse<ApiResponse<ServiceCategory>> = await this.api.put(`/service-categories/${id}`, data);
    return response.data.data;
  }

  async deleteServiceCategory(id: number, action?: 'block' | 'move' | 'deactivate', targetCategoryId?: number): Promise<{ success: boolean; message: string; data?: any }> {
    const params: any = {};
    if (action) params.action = action;
    if (targetCategoryId) params.target_category_id = targetCategoryId;
    
    const response: AxiosResponse<{ success: boolean; message: string; data?: any }> = await this.api.delete(`/service-categories/${id}`, { params });
    // Backend returns { success, message, data } directly, not wrapped in data
    return {
      success: response.data.success ?? true,
      message: response.data.message || 'Service category deleted successfully',
      data: response.data.data
    };
  }

  async getServicesByCategory(categoryId: number): Promise<ProfessionalService[]> {
    const response: AxiosResponse<ApiResponse<ProfessionalService[]>> = await this.api.get(`/service-categories/${categoryId}/services`);
    return response.data.data;
  }

  // Professional Services API
  async getProfessionalServices(params?: ServiceFilterParams): Promise<PaginatedResponse<ProfessionalService>> {
    const response: AxiosResponse<ApiResponse<{ services: ProfessionalService[], pagination: any }>> = await this.api.get('/professional-services', {
      params
    });
    return {
      data: response.data.data.services,
      pagination: response.data.data.pagination
    };
  }

  async getProfessionalService(id: number): Promise<ProfessionalService> {
    const response: AxiosResponse<ApiResponse<ProfessionalService>> = await this.api.get(`/professional-services/${id}`);
    return response.data.data;
  }

  async createProfessionalService(data: CreateProfessionalServiceRequest): Promise<ProfessionalService> {
    const response: AxiosResponse<ApiResponse<ProfessionalService>> = await this.api.post('/professional-services', data);
    return response.data.data;
  }

  async updateProfessionalService(id: number, data: UpdateProfessionalServiceRequest): Promise<ProfessionalService> {
    const response: AxiosResponse<ApiResponse<ProfessionalService>> = await this.api.put(`/professional-services/${id}`, data);
    return response.data.data;
  }

  async deleteProfessionalService(id: number): Promise<void> {
    await this.api.delete(`/professional-services/${id}`);
  }

  async searchProfessionalServices(params: ServiceSearchParams): Promise<PaginatedResponse<ProfessionalService>> {
    const response: AxiosResponse<ApiResponse<{ services: ProfessionalService[], pagination: any }>> = await this.api.get('/professional-services/search', {
      params
    });
    return {
      data: response.data.data.services,
      pagination: response.data.data.pagination
    };
  }

  async getPendingVerificationServices(params?: { per_page?: number; page?: number }): Promise<PaginatedResponse<ProfessionalService>> {
    const response: AxiosResponse<ApiResponse<{ services: ProfessionalService[], pagination: any }>> = await this.api.get('/professional-services/pending-verification', {
      params
    });
    return {
      data: response.data.data.services,
      pagination: response.data.data.pagination
    };
  }

  async verifyService(id: number, data: VerifyServiceRequest): Promise<ProfessionalService> {
    const response: AxiosResponse<ApiResponse<ProfessionalService>> = await this.api.post(`/professional-services/${id}/verify`, data);
    return response.data.data;
  }

  // Bookings Analytics API (admin/professional/patient; backend scopes automatically)
  async getBookingAnalytics(params?: { date_from?: string; date_to?: string }): Promise<BookingAnalytics> {
    const response: AxiosResponse<ApiResponse<BookingAnalytics>> = await this.api.get('/bookings/analytics', { params });
    return response.data.data;
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response: AxiosResponse<ApiResponse<{ status: string; timestamp: string }>> = await this.api.get('/health');
    return response.data.data;
  }

  // Payment API
  async getPayments(params?: PaymentFilterParams): Promise<PaginatedResponse<Payment>> {
    const response: AxiosResponse<ApiResponse<{ payments: Payment[], pagination: any }>> = await this.api.get('/payments', {
      params
    });
    return {
      data: response.data.data.payments,
      pagination: response.data.data.pagination
    };
  }

  async getPayment(id: number): Promise<Payment> {
    const response: AxiosResponse<ApiResponse<Payment>> = await this.api.get(`/payments/${id}`);
    return response.data.data;
  }

  // Note: Payment stats are calculated client-side from payments list
  // This method is kept for future backend implementation
  async getPaymentStats(): Promise<PaymentStats> {
    // For now, calculate from payments
    const paymentsResponse = await this.getPayments({ per_page: 1000 });
    const payments = paymentsResponse.data;
    
    return {
      total_payments: payments.length,
      total_amount: payments.reduce((sum, p) => sum + parseFloat(p.amount.toString()), 0),
      total_commission: payments.reduce((sum, p) => sum + parseFloat(p.commission_amount.toString()), 0),
      pending_payments: payments.filter(p => p.status === 'pending' || p.status === 'processing').length,
      completed_payments: payments.filter(p => p.status === 'completed').length,
      failed_payments: payments.filter(p => p.status === 'failed' || p.status === 'cancelled').length,
      escrow_held: payments.filter(p => p.escrow_status === 'held').reduce((sum, p) => sum + parseFloat(p.amount.toString()), 0),
      escrow_released: payments.filter(p => p.escrow_status === 'released').reduce((sum, p) => sum + parseFloat(p.amount.toString()), 0),
    };
  }

  async refundPayment(id: number, reason?: string): Promise<Payment> {
    const response: AxiosResponse<ApiResponse<Payment>> = await this.api.post(`/payments/${id}/refund`, {
      reason
    });
    return response.data.data;
  }

  async releaseEscrow(id: number): Promise<Payment> {
    const response: AxiosResponse<ApiResponse<Payment>> = await this.api.post(`/payments/${id}/release-escrow`);
    return response.data.data;
  }

  // Payout Request API
  async getPayoutRequests(params?: { status?: string; per_page?: number; page?: number }): Promise<PaginatedResponse<PayoutRequest>> {
    const response: AxiosResponse<ApiResponse<{ payout_requests: PayoutRequest[], pagination: any }>> = await this.api.get('/payout-requests', {
      params
    });
    return {
      data: response.data.data.payout_requests || [],
      pagination: response.data.data.pagination || {}
    };
  }

  async getPayoutRequest(id: number): Promise<PayoutRequest> {
    const response: AxiosResponse<ApiResponse<PayoutRequest>> = await this.api.get(`/payout-requests/${id}`);
    return response.data.data;
  }

  async approvePayoutRequest(id: number, data: ApprovePayoutRequest): Promise<PayoutRequest> {
    const response: AxiosResponse<ApiResponse<PayoutRequest>> = await this.api.post(`/payout-requests/${id}/approve`, data);
    return response.data.data;
  }

  // Wallet API
  async getWallets(params?: { per_page?: number; page?: number }): Promise<PaginatedResponse<Wallet>> {
    const response: AxiosResponse<ApiResponse<{ wallets: Wallet[], pagination: any }>> = await this.api.get('/admin/wallets', {
      params
    });
    return {
      data: response.data.data.wallets || [],
      pagination: response.data.data.pagination || {}
    };
  }

  async getWalletTransactions(walletId: number, params?: { type?: string; per_page?: number; page?: number }): Promise<PaginatedResponse<WalletTransaction>> {
    const response: AxiosResponse<ApiResponse<{ transactions: WalletTransaction[], pagination: any }>> = await this.api.get(`/admin/wallets/${walletId}/transactions`, {
      params
    });
    return {
      data: response.data.data.transactions || [],
      pagination: response.data.data.pagination || {}
    };
  }

  // Commission API
  async getCommissionStats(params?: { date_from?: string; date_to?: string }): Promise<CommissionStats> {
    const response: AxiosResponse<ApiResponse<CommissionStats>> = await this.api.get('/admin/commissions/stats', {
      params
    });
    return response.data.data;
  }

  // Financial Reports API
  async getFinancialReport(params: { period: 'daily' | 'weekly' | 'monthly' | 'yearly'; date_from?: string; date_to?: string }): Promise<FinancialReport> {
    const response: AxiosResponse<ApiResponse<FinancialReport>> = await this.api.get('/admin/financial-reports', {
      params
    });
    return response.data.data;
  }

  // V3.2.1 Admin APIs - Subscription Plans
  async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    const response: AxiosResponse<ApiResponse<SubscriptionPlan[]>> = await this.api.get('/admin/subscription-plans');
    return response.data.data;
  }

  async getSubscriptionPlan(id: number): Promise<SubscriptionPlan> {
    const response: AxiosResponse<ApiResponse<SubscriptionPlan>> = await this.api.get(`/admin/subscription-plans/${id}`);
    return response.data.data;
  }

  async createSubscriptionPlan(data: CreateSubscriptionPlanRequest): Promise<SubscriptionPlan> {
    const response: AxiosResponse<ApiResponse<SubscriptionPlan>> = await this.api.post('/admin/subscription-plans', data);
    return response.data.data;
  }

  async updateSubscriptionPlan(id: number, data: UpdateSubscriptionPlanRequest): Promise<SubscriptionPlan> {
    const response: AxiosResponse<ApiResponse<SubscriptionPlan>> = await this.api.put(`/admin/subscription-plans/${id}`, data);
    return response.data.data;
  }

  async deleteSubscriptionPlan(id: number): Promise<void> {
    await this.api.delete(`/admin/subscription-plans/${id}`);
  }

  async getSubscriptionStatistics(params?: { period?: 'daily' | 'weekly' | 'monthly' | 'yearly' }): Promise<SubscriptionStatistics> {
    const response: AxiosResponse<ApiResponse<SubscriptionStatistics>> = await this.api.get('/admin/subscription-statistics', {
      params,
    });
    return response.data.data;
  }

  async getPaymentStatistics(params?: { page?: number; per_page?: number }): Promise<PaymentStatistics> {
    const response: AxiosResponse<ApiResponse<PaymentStatistics>> = await this.api.get('/payments/statistics', {
      params,
    });
    return response.data.data;
  }

  async getPaymentTransactions(params?: {
    page?: number;
    per_page?: number;
    date_from?: string;
    date_to?: string;
    status?: 'success' | 'failure' | 'cancelled';
  }): Promise<PaymentTransactionsResponse> {
    const response: AxiosResponse<ApiResponse<PaymentTransactionsResponse>> = await this.api.get(
      '/payments/transactions',
      { params }
    );
    return response.data.data;
  }

  async exportPaymentTransactions(params?: {
    date_from?: string;
    date_to?: string;
    status?: 'success' | 'failure' | 'cancelled';
  }): Promise<Blob> {
    const response = await this.api.get('/payments/transactions', {
      params: { ...params, export: 1 },
      responseType: 'blob',
    });
    return response.data;
  }

  async getAllSubscriptions(params?: { status?: string; subscription_type?: string; user_id?: number; per_page?: number; page?: number }): Promise<PaginatedResponse<Subscription>> {
    const response: AxiosResponse<ApiResponse<{ subscriptions: Subscription[], pagination: any }>> = await this.api.get('/admin/subscriptions', {
      params
    });
    return {
      data: response.data.data.subscriptions || [],
      pagination: response.data.data.pagination || {}
    };
  }

  // V3.2.1 Admin APIs - Leads
  async getAdminLeads(params?: { status?: string; source?: string; professional_id?: number; patient_id?: number; category_id?: number; date_from?: string; date_to?: string; per_page?: number; page?: number; search?: string }): Promise<PaginatedResponse<Lead>> {
    const response: AxiosResponse<ApiResponse<{ leads: Lead[], pagination: any }>> = await this.api.get('/admin/leads', {
      params
    });
    return {
      data: response.data.data.leads || [],
      pagination: response.data.data.pagination || {}
    };
  }

  async getAdminLead(id: number): Promise<Lead> {
    // Backend returns { data: { lead: Lead, sla_status: {...} } }
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/admin/leads/${id}`);
    const data = response.data.data;
    if (data && typeof data === 'object' && data.lead) {
      return data.lead as Lead;
    }
    return data as Lead;
  }

  async getLeadStatistics(): Promise<LeadStatistics> {
    const response: AxiosResponse<ApiResponse<LeadStatistics>> = await this.api.get('/admin/lead-statistics');
    return response.data.data;
  }

  async getSLAMonitoring(): Promise<SLAMonitoring> {
    const response: AxiosResponse<ApiResponse<SLAMonitoring>> = await this.api.get('/admin/sla-monitoring');
    return response.data.data;
  }

  // V3.2.1 Admin APIs - Contact Reveal Events
  async getContactRevealEvents(params?: { event_type?: string; professional_id?: number; patient_id?: number; subscription_id?: number; date_from?: string; date_to?: string; per_page?: number; page?: number }): Promise<PaginatedResponse<ContactRevealEvent>> {
    const response: AxiosResponse<ApiResponse<{ events: ContactRevealEvent[], pagination: any }>> = await this.api.get('/admin/contact-reveal-events', {
      params
    });
    return {
      data: response.data.data.events || [],
      pagination: response.data.data.pagination || {}
    };
  }

  async getEventStatistics(): Promise<EventStatistics> {
    const response: AxiosResponse<ApiResponse<EventStatistics>> = await this.api.get('/admin/contact-reveal-events/statistics');
    return response.data.data;
  }

  // Documents API (Admin)
  async getDocumentsByUser(userId: number): Promise<Document[]> {
    const response: AxiosResponse<ApiResponse<{ documents: Document[] }>> = await this.api.get('/documents/by-user', {
      params: { user_id: userId }
    });
    // Handle both response formats
    if (response.data.data.documents) {
    return response.data.data.documents;
    }
    // Fallback if documents are directly in data
    if (Array.isArray(response.data.data)) {
      return response.data.data;
    }
    return [];
  }

  // Admin Comments API
  async getAdminComments(professionalId: number, commentType?: string): Promise<AdminComment[]> {
    const response: AxiosResponse<ApiResponse<{ comments: AdminComment[] }>> = await this.api.get('/admin/comments', {
      params: {
        professional_id: professionalId,
        comment_type: commentType,
      }
    });
    return response.data.data.comments;
  }

  async createAdminComment(data: CreateAdminCommentRequest): Promise<AdminComment> {
    const response: AxiosResponse<ApiResponse<{ comment: AdminComment }>> = await this.api.post('/admin/comments', data);
    return response.data.data.comment;
  }

  async updateAdminComment(id: number, comment: string): Promise<AdminComment> {
    const response: AxiosResponse<ApiResponse<{ comment: AdminComment }>> = await this.api.put(`/admin/comments/${id}`, {
      comment,
    });
    return response.data.data.comment;
  }

  async deleteAdminComment(id: number): Promise<void> {
    await this.api.delete(`/admin/comments/${id}`);
  }

  // Admin Activity Logs
  async logAdminActivity(data: Omit<AdminActivityLog, 'id' | 'created_at' | 'updated_at'>): Promise<AdminActivityLog> {
    const response: AxiosResponse<ApiResponse<AdminActivityLog>> = await this.api.post('/admin/activity-logs', data);
    return response.data.data;
  }

  async getAdminActivityLogs(params?: {
    page?: number;
    per_page?: number;
    severity?: 'info' | 'success' | 'warning' | 'error';
    source?: string;
    message?: string;
    user_id?: number;
    booking_id?: number;
    admin_only?: boolean;
    notification_category?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<{
    data: AdminActivityLog[];
    pagination: PaginationMeta;
    unread_count: number;
    last_seen?: string | null;
  }> {
    const response: AxiosResponse<ApiResponse<{ logs: AdminActivityLog[]; pagination: any; unread_count: number; last_seen: string | null }>> = await this.api.get('/admin/activity-logs', {
      params,
    });
    return {
      data: response.data.data.logs,
      pagination: response.data.data.pagination,
      unread_count: response.data.data.unread_count ?? 0,
      last_seen: response.data.data.last_seen || null,
    };
  }

  async getAdminProfileApprovalRequests(params?: {
    profile_id?: number;
    status?: string;
    per_page?: number;
    page?: number;
  }): Promise<PaginatedResponse<ProfileApprovalRequest>> {
    const response: AxiosResponse<ApiResponse<{ approval_requests: ProfileApprovalRequest[]; pagination: any }>> =
      await this.api.get('/admin/profile-approval-requests', { params });

    return {
      data: response.data.data.approval_requests,
      pagination: response.data.data.pagination,
    };
  }

  async approveProfileApprovalRequest(requestId: number): Promise<ProfileApprovalRequest> {
    const response: AxiosResponse<ApiResponse<{ approval_request: ProfileApprovalRequest }>> =
      await this.api.post(`/admin/profile-approval-requests/${requestId}/approve`);
    return response.data.data.approval_request;
  }

  async rejectProfileApprovalRequest(requestId: number, rejectionReason: string): Promise<ProfileApprovalRequest> {
    const response: AxiosResponse<ApiResponse<{ approval_request: ProfileApprovalRequest }>> =
      await this.api.post(`/admin/profile-approval-requests/${requestId}/reject`, {
        rejection_reason: rejectionReason,
      });
    return response.data.data.approval_request;
  }

  // Bulk Field Update Requests (Admin)
  async getAdminFieldUpdateRequests(params?: {
    status?: 'pending' | 'partially_approved' | 'approved' | 'rejected';
    profile_id?: number;
    user_id?: number;
    per_page?: number;
    page?: number;
  }): Promise<PaginatedResponse<any>> {
    const response: AxiosResponse<ApiResponse<{ requests: any[]; pagination: any }>> =
      await this.api.get('/admin/profile-field-update-requests', {
        params,
      });
    return {
      data: response.data.data.requests || [],
      pagination: response.data.data.pagination,
    };
  }

  async getAdminFieldUpdateRequest(requestId: number): Promise<any> {
    const response: AxiosResponse<ApiResponse<{ request: any }>> =
      await this.api.get(`/admin/profile-field-update-requests/${requestId}`);
    return response.data.data.request;
  }

  async approveFieldUpdateRequestItem(itemId: number): Promise<any> {
    const response: AxiosResponse<ApiResponse<{ item: any }>> =
      await this.api.post(`/admin/profile-field-update-request-items/${itemId}/approve`);
    return response.data.data.item;
  }

  async rejectFieldUpdateRequestItem(itemId: number, rejectionReason: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<{ item: any }>> =
      await this.api.post(`/admin/profile-field-update-request-items/${itemId}/reject`, {
        rejection_reason: rejectionReason,
      });
    return response.data.data.item;
  }

  async getNotifications(params?: {
    page?: number;
    per_page?: number;
    severity?: 'success' | 'error' | 'warning' | 'info';
    source?: string;
    message?: string;
    booking_id?: number;
  }): Promise<{
    data: NotificationEntry[];
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
    };
    last_seen: string | null;
  }> {
    const response: AxiosResponse<ApiResponse<{ logs: NotificationEntry[]; pagination: any; last_seen: string | null }>> =
      await this.api.get('/notifications', { params });

    return {
      data: response.data.data.logs || [],
      pagination: response.data.data.pagination,
      last_seen: response.data.data.last_seen || null,
    };
  }

  async markNotificationsRead(): Promise<string | null> {
    const response: AxiosResponse<ApiResponse<{ last_seen: string | null }>> = await this.api.post('/notifications/mark-read');
    return response.data.data.last_seen || null;
  }

  async getAdminProfessionalReplies(params?: {
    page?: number;
    per_page?: number;
    professional_id?: number;
    comment_type?: 'verification' | 'general' | 'document' | 'service';
    source?: string;
    message?: string;
  }): Promise<{
    data: ProfessionalReplyNotificationEntry[];
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
    };
    last_seen: string | null;
  }> {
    const response: AxiosResponse<ApiResponse<{ logs: ProfessionalReplyNotificationEntry[]; pagination: any; last_seen: string | null }>> =
      await this.api.get('/admin/professional-replies', { params });

    return {
      data: response.data.data.logs || [],
      pagination: response.data.data.pagination,
      last_seen: response.data.data.last_seen || null,
    };
  }

  // Professional Comments API (for professionals to view and reply)
  async getMyComments(commentType?: string): Promise<AdminComment[]> {
    const response: AxiosResponse<ApiResponse<{ comments: AdminComment[] }>> = await this.api.get('/comments', {
      params: {
        comment_type: commentType,
      }
    });
    return response.data.data.comments;
  }

  async replyToComment(commentId: number, reply: string): Promise<AdminComment> {
    const response: AxiosResponse<ApiResponse<{ comment: AdminComment }>> = await this.api.post(`/comments/${commentId}/reply`, {
      reply,
    });
    return response.data.data.comment;
  }

  async markCommentAsRead(commentId: number): Promise<AdminComment> {
    const response: AxiosResponse<ApiResponse<{ comment: AdminComment }>> = await this.api.post(`/comments/${commentId}/mark-read`);
    return response.data.data.comment;
  }

  // Document Approval/Rejection API (Admin only)
  async approveDocument(documentId: number): Promise<Document> {
    const response: AxiosResponse<ApiResponse<{ document: Document }>> = await this.api.post(`/documents/${documentId}/approve`);
    return response.data.data.document;
  }

  async rejectDocument(documentId: number, rejectionReason: string): Promise<Document> {
    const response: AxiosResponse<ApiResponse<{ document: Document }>> = await this.api.post(`/documents/${documentId}/reject`, {
      rejection_reason: rejectionReason,
    });
    return response.data.data.document;
  }

  // System Settings API
  async getPaymentSettings(): Promise<{ 
    default: { commission_percentage: number; platform_fee: number };
    category_commissions: Array<{ category_id: number; category_name: string; commission_percentage: number; platform_fee: number }>;
    categories: Array<{ id: number; name: string }>;
  }> {
    const response: AxiosResponse<ApiResponse<{ 
      default: { commission_percentage: number; platform_fee: number };
      category_commissions: Array<{ category_id: number; category_name: string; commission_percentage: number; platform_fee: number }>;
      categories: Array<{ id: number; name: string }>;
    }>> = await this.api.get('/admin/settings/payment');
    return response.data.data;
  }

  async updatePaymentSettings(settings: { 
    default?: { commission_percentage: number; platform_fee: number };
    category_commissions?: Array<{ category_id: number; commission_percentage: number; platform_fee: number }>;
  }): Promise<{ 
    default: { commission_percentage: number; platform_fee: number };
    category_commissions: Array<{ category_id: number; category_name: string; commission_percentage: number; platform_fee: number }>;
    categories: Array<{ id: number; name: string }>;
  }> {
    const response: AxiosResponse<ApiResponse<{ 
      default: { commission_percentage: number; platform_fee: number };
      category_commissions: Array<{ category_id: number; category_name: string; commission_percentage: number; platform_fee: number }>;
      categories: Array<{ id: number; name: string }>;
    }>> = await this.api.put('/admin/settings/payment', settings);
    return response.data.data;
  }

  async getLeadManagementSettings(): Promise<{
    fast_responder_threshold: number;
    default_lead_auto_closure_days: number;
  }> {
    const response: AxiosResponse<ApiResponse<{
      fast_responder_threshold: number;
      default_lead_auto_closure_days: number;
    }>> = await this.api.get('/admin/settings/lead-management');
    return response.data.data;
  }

  async updateLeadManagementSettings(settings: {
    fast_responder_threshold?: number;
    default_lead_auto_closure_days?: number;
  }): Promise<{
    fast_responder_threshold: number;
    default_lead_auto_closure_days: number;
  }> {
    const response: AxiosResponse<ApiResponse<{
      fast_responder_threshold: number;
      default_lead_auto_closure_days: number;
    }>> = await this.api.put('/admin/settings/lead-management', settings);
    return response.data.data;
  }

  async getNotificationSettings(): Promise<{ push_notifications_enabled: boolean }> {
    const response: AxiosResponse<ApiResponse<{ push_notifications_enabled: boolean }>> =
      await this.api.get('/admin/settings/notifications');
    return response.data.data;
  }

  async updateNotificationSettings(settings: {
    push_notifications_enabled?: boolean;
  }): Promise<{ push_notifications_enabled: boolean }> {
    const response: AxiosResponse<ApiResponse<{ push_notifications_enabled: boolean }>> =
      await this.api.put('/admin/settings/notifications', settings);
    return response.data.data;
  }

  async getSecuritySettings(): Promise<{ min_password_length: number }> {
    const response: AxiosResponse<ApiResponse<{ min_password_length: number }>> =
      await this.api.get('/admin/settings/security');
    return response.data.data;
  }

  async updateSecuritySettings(settings: {
    min_password_length?: number;
  }): Promise<{ min_password_length: number }> {
    const response: AxiosResponse<ApiResponse<{ min_password_length: number }>> =
      await this.api.put('/admin/settings/security', settings);
    return response.data.data;
  }

  // City Management API
  async getCities(): Promise<{ data: City[]; success: boolean }> {
    try {
      const response = await this.api.get('/admin/cities');
      // Backend returns { success: true, data: [...] }
      return {
        data: response.data.data || [],
        success: response.data.success || true,
      };
    } catch (error: any) {
      console.error('Error fetching cities:', error);
      return {
        data: [],
        success: false,
      };
    }
  }

  async createCity(city: Partial<City>): Promise<{ data: City | null; success: boolean }> {
    try {
      const response = await this.api.post('/admin/cities', city);
      return {
        data: response.data.data || response.data,
        success: response.data.success || true,
      };
    } catch (error: any) {
      console.error('Error creating city:', error);
      return {
        data: null,
        success: false,
      };
    }
  }

  async updateCity(id: number, city: Partial<City>): Promise<{ data: City | null; success: boolean }> {
    try {
      const response = await this.api.put(`/admin/cities/${id}`, city);
      return {
        data: response.data.data || response.data,
        success: response.data.success || true,
      };
    } catch (error: any) {
      console.error('Error updating city:', error);
      return {
        data: null,
        success: false,
      };
    }
  }

  async deleteCity(id: number): Promise<{ success: boolean }> {
    try {
      const response = await this.api.delete(`/admin/cities/${id}`);
      return {
        success: response.data.success || true,
      };
    } catch (error: any) {
      console.error('Error deleting city:', error);
      return {
        success: false,
      };
    }
  }

  // Default City API
  async getDefaultCity(): Promise<string> {
    const response = await this.api.get('/admin/settings/default-city');
    return response.data?.data || 'Mumbai';
  }

  async setDefaultCity(city: string): Promise<{ success: boolean; message: string; data?: string }> {
    const response = await this.api.put('/admin/settings/default-city', { city });
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
